import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle, XCircle, ArrowsClockwise, Copy, GoogleLogo, FacebookLogo, Storefront, WhatsappLogo,
} from "@phosphor-icons/react";

export default function Integrations() {
  const [status, setStatus] = useState(null);
  const [appsScript, setAppsScript] = useState(null);
  const [showScript, setShowScript] = useState(false);
  const [syncing, setSyncing] = useState({ indiamart: false, meta: false });
  const [refreshingToken, setRefreshingToken] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);

  const loadStatus = () => api.get("/integrations/status").then((r) => setStatus(r.data));
  const loadSyncStatus = () => api.get("/integrations/sync-status").then((r) => setSyncStatus(r.data)).catch(() => { });

  const refreshMetaToken = async () => {
    setRefreshingToken(true);
    try {
      const r = await api.post("/integrations/meta/refresh-tokens");
      toast.success(`Meta tokens refreshed · ${r.data.pages} page(s) connected`);
      await loadSyncStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed");
    } finally {
      setRefreshingToken(false);
    }
  };

  const connectMeta = () => {
    if (!window.FB) {
      toast.error("Facebook SDK not loaded yet. Please try again in a moment.");
      return;
    }
    window.FB.login((response) => {
      if (response.authResponse) {
        toast.info("Token received. Authenticating with server...");
        api.post("/integrations/meta/oauth", {
          short_lived_token: response.authResponse.accessToken
        }).then((r) => {
          toast.success(`Meta connected successfully! ${r.data.pages} page(s) found.`);
          loadStatus();
          loadSyncStatus();
        }).catch((e) => {
          toast.error(e.response?.data?.detail || "OAuth exchange failed");
        });
      } else {
        toast.error("Facebook login was cancelled or failed.");
      }
    }, {
      scope: "leads_retrieval,pages_show_list,pages_read_engagement,pages_manage_metadata"
    });
  };

  useEffect(() => {
    loadStatus(); loadSyncStatus();
    if (!document.getElementById("fb-root")) {
      const fbRoot = document.createElement("div");
      fbRoot.id = "fb-root";
      document.body.appendChild(fbRoot);

      window.fbAsyncInit = function () {
        window.FB.init({
          appId: process.env.REACT_APP_META_APP_ID,
          cookie: true,
          xfbml: true,
          version: 'v19.0'
        });
      };

      const script = document.createElement("script");
      script.src = "https://connect.facebook.net/en_US/sdk.js";
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
  }, []);

  const fetchAppsScript = async () => {
    const r = await api.get(`/integrations/google-form/apps-script`, {
      params: { public_url: window.location.origin },
    });
    setAppsScript(r.data);
    setShowScript(true);
  };

  const copy = async (text, label) => {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const syncNow = async (kind) => {
    setSyncing((s) => ({ ...s, [kind]: true }));
    try {
      const r = await api.post(`/leads/import/${kind}`);
      if (r.data?.started || r.data?.running) {
        toast.success(`${kind} sync started in background — you'll see new leads once it finishes`);
        pollSyncStatus(kind);
      } else {
        toast.success(`Imported ${Array.isArray(r.data) ? r.data.length : 0} new ${kind} leads`);
        setSyncing((s) => ({ ...s, [kind]: false }));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
      setSyncing((s) => ({ ...s, [kind]: false }));
    }
  };

  const pollSyncStatus = (kind) => {
    const start = Date.now();
    const tick = async () => {
      try {
        const r = await api.get("/integrations/sync-status");
        const s = r.data[kind];
        if (s && !s.running) {
          setSyncing((cur) => ({ ...cur, [kind]: false }));
          if (s.last_error) toast.error(`${kind} sync failed: ${s.last_error.slice(0, 160)}`);
          else toast.success(`${kind} sync complete · ${s.last_count} new leads added`);
          return;
        }
        if (Date.now() - start > 5 * 60_000) {
          setSyncing((cur) => ({ ...cur, [kind]: false }));
          return;
        }
        setTimeout(tick, 4000);
      } catch {
        setSyncing((cur) => ({ ...cur, [kind]: false }));
      }
    };
    setTimeout(tick, 3000);
  };

  if (!status) return <div className="text-[#6B7280]">Loading...</div>;

  return (
    <div data-testid="integrations-page" className="max-w-4xl">
      <div className="mb-8">
        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#6B7280] mb-2">Lead Sources & Channels</div>
        <h1 className="font-heading font-black text-3xl sm:text-5xl tracking-tighter">Integrations</h1>
        <p className="text-sm text-[#6B7280] mt-2">Connect lead sources and messaging channels. Live polling runs every 12 hours.</p>
      </div>

      <div className="grid gap-4">
        {/* IndiaMART */}
        <IntegrationCard
          icon={Storefront}
          title="IndiaMART Lead Manager"
          subtitle="Pull API v2 · polled every 12 hours"
          connected={status.indiamart.configured}
          testid="card-indiamart"
        >
          {status.indiamart.configured ? (
            <p className="text-sm text-[#374151]">CRM key is configured. New leads from IndiaMART will be pulled automatically.</p>
          ) : (
            <ol className="text-sm text-[#374151] space-y-1 list-decimal list-inside">
              <li>Log in to <span className="font-mono">seller.indiamart.com</span></li>
              <li>Lead Manager → ☰ menu → <b>Import/Export Leads</b> → <b>Pull API</b></li>
              <li>Click <b>Generate Key</b> and copy it</li>
              <li>Share the CRM key with your admin — they&apos;ll add it to <span className="font-mono">INDIAMART_CRM_KEY</span> in backend env</li>
            </ol>
          )}
          <div className="mt-3 flex gap-2">
            <button onClick={() => syncNow("indiamart")} disabled={syncing.indiamart} data-testid="sync-indiamart"
              className="border border-[#D1D5DB] px-4 py-2 text-sm font-bold hover:bg-[#F3F4F6] flex items-center gap-2 disabled:opacity-50">
              <ArrowsClockwise size={14} weight="bold" /> {syncing.indiamart ? "Syncing..." : "Sync Now"}
            </button>
            {!status.indiamart.configured && (
              <span className="text-xs text-[#6B7280] self-center">→ Sync now will use sample mock data until your key is added.</span>
            )}
          </div>
        </IntegrationCard>

        {/* Meta */}
        <IntegrationCard
          icon={FacebookLogo}
          title="Meta Lead Ads (Facebook + Instagram)"
          subtitle="Graph API · polled every 12 hours"
          connected={status.meta.configured}
          testid="card-meta"
        >
          {status.meta.configured ? (
            <>
              <p className="text-sm text-[#374151]">Page Access Token configured.</p>
              {syncStatus?.meta_tokens && (
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                  <Stat label="Pages" value={syncStatus.meta_tokens.page_count} />
                  <Stat label="Last refresh" value={syncStatus.meta_tokens.refreshed_at ? new Date(syncStatus.meta_tokens.refreshed_at).toLocaleString('en-IN') : "—"} />
                  <Stat label="Token expires" value={syncStatus.meta_tokens.expires_at ? new Date(syncStatus.meta_tokens.expires_at * 1000).toLocaleDateString('en-IN') : "—"} />
                </div>
              )}
            </>
          ) : (
            <div className="text-sm text-[#374151] space-y-3">
              <p>Connect your Meta account to automatically sync leads from your Facebook and Instagram Lead Ads campaigns.</p>
              <button onClick={connectMeta} data-testid="connect-meta-oauth"
                className="bg-[#1877F2] text-white px-5 py-2.5 text-sm font-bold flex items-center gap-2 hover:bg-[#166FE5]">
                <FacebookLogo size={18} weight="fill" /> Connect with Facebook
              </button>
            </div>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => syncNow("meta")} disabled={syncing.meta} data-testid="sync-meta"
              className="border border-[#D1D5DB] px-4 py-2 text-sm font-bold hover:bg-[#F3F4F6] flex items-center gap-2 disabled:opacity-50">
              <ArrowsClockwise size={14} weight="bold" /> {syncing.meta ? "Syncing..." : "Sync Now"}
            </button>
            {status.meta.configured && (
              <>
                <button onClick={refreshMetaToken} disabled={refreshingToken} data-testid="refresh-meta-token"
                  className="border border-[#D1D5DB] px-4 py-2 text-sm font-bold hover:bg-[#F3F4F6] flex items-center gap-2 disabled:opacity-50">
                  <ArrowsClockwise size={14} weight="bold" /> {refreshingToken ? "Refreshing..." : "Refresh Token"}
                </button>
                <button onClick={connectMeta} data-testid="reconnect-meta"
                  className="border border-[#D1D5DB] text-[#1877F2] px-4 py-2 text-sm font-bold hover:bg-[#F3F4F6] flex items-center gap-2">
                  <FacebookLogo size={14} weight="fill" /> Reconnect Facebook
                </button>
              </>
            )}
            {!status.meta.configured && <span className="text-xs text-[#6B7280] self-center">→ Sync now will use sample mock data until connected.</span>}
          </div>
        </IntegrationCard>

        {/* Google Form */}
        <IntegrationCard
          icon={GoogleLogo}
          title="Google Forms"
          subtitle="Webhook ingestion — Apps Script pushes each submission"
          connected={true}
          testid="card-googleform"
        >
          <p className="text-sm text-[#374151] mb-3">
            Use any Google Form you already have. We give you a small script — paste it into the form&apos;s
            Apps Script, and every submission shows up here as a lead.
          </p>
          <button onClick={fetchAppsScript} data-testid="get-apps-script"
            className="bg-[#111827] text-white px-4 py-2 text-sm font-bold hover:bg-[#1F2937] flex items-center gap-2">
            Get setup snippet
          </button>

          {showScript && appsScript && (
            <div className="mt-4 border border-[#E5E7EB] p-4 bg-[#F9FAFB]" data-testid="apps-script-block">
              <div className="grid sm:grid-cols-2 gap-3 mb-3">
                <KV label="Ingest URL" value={appsScript.ingest_url} onCopy={() => copy(appsScript.ingest_url, "URL")} />
                <KV label="Token" value={appsScript.token} onCopy={() => copy(appsScript.token, "Token")} mono />
              </div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280] mb-2 flex justify-between items-center">
                <span>Apps Script</span>
                <button onClick={() => copy(appsScript.script, "Script")} data-testid="copy-script"
                  className="border border-[#D1D5DB] px-2 py-1 text-[10px] font-bold flex items-center gap-1 normal-case hover:bg-white">
                  <Copy size={12} weight="bold" /> Copy
                </button>
              </div>
              <pre className="bg-[#111827] text-[#E5E7EB] p-3 text-xs overflow-x-auto whitespace-pre font-mono max-h-72">
                {appsScript.script}
              </pre>
              <ol className="text-xs text-[#374151] mt-3 space-y-1 list-decimal list-inside">
                <li>Open your Google Form → <b>Extensions → Apps Script</b></li>
                <li>Paste the script above (replace any existing code), Save (💾)</li>
                <li>Click <b>Run</b> once → grant the prompted permissions</li>
                <li>In Apps Script → ⏰ Triggers → <b>Add Trigger</b> → function <span className="font-mono">onFormSubmit</span>, event source <b>From form</b>, event type <b>On form submit</b></li>
                <li>Done — submit a test response to verify a lead appears in LeadPilot</li>
              </ol>
            </div>
          )}
        </IntegrationCard>

      </div>
    </div>
  );
}

function IntegrationCard({ icon: Icon, title, subtitle, connected, children, testid }) {
  return (
    <section className="border border-[#E5E7EB] bg-white" data-testid={testid}>
      <div className="flex items-start justify-between gap-3 p-5 border-b border-[#F3F4F6]">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-10 h-10 bg-[#111827] text-white flex items-center justify-center shrink-0">
            <Icon size={20} weight="bold" />
          </div>
          <div className="min-w-0">
            <h2 className="font-heading font-bold text-lg sm:text-xl tracking-tight truncate">{title}</h2>
            <p className="text-xs text-[#6B7280]">{subtitle}</p>
          </div>
        </div>
        <ConnectedBadge connected={connected} />
      </div>
      <div className="p-5">{children}</div>
    </section>
  );
}

function ConnectedBadge({ connected }) {
  return connected ? (
    <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 border border-[#10B981] text-[#10B981] flex items-center gap-1 shrink-0">
      <CheckCircle size={12} weight="fill" /> Connected
    </span>
  ) : (
    <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 border border-[#6B7280] text-[#6B7280] flex items-center gap-1 shrink-0">
      <XCircle size={12} weight="fill" /> Not configured
    </span>
  );
}

function KV({ label, value, onCopy, mono }) {
  return (
    <div className="border border-[#E5E7EB] bg-white p-2 flex items-center justify-between gap-2 min-w-0">
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280]">{label}</div>
        <div className={`text-xs truncate ${mono ? "font-mono" : ""}`}>{value}</div>
      </div>
      <button onClick={onCopy} className="border border-[#D1D5DB] px-2 py-1 hover:bg-[#F3F4F6] shrink-0">
        <Copy size={12} weight="bold" />
      </button>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-[#E5E7EB] bg-[#F9FAFB] p-2 min-w-0">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#6B7280]">{label}</div>
      <div className="text-xs font-mono truncate">{value}</div>
    </div>
  );
}
